/**
 * Identidade do dispositivo no widget.
 *
 * O ID vive no localStorage da ORIGEM corrente — no embed é o domínio do
 * cliente, na hosted é `widget.<domínio>`. A mesma pessoa no mesmo celular
 * gera IDs diferentes nas duas origens: isso é esperado e aceito (unificar
 * exigiria fingerprinting). A agregação acontece pelo contato.
 *
 * A chave NUNCA deve ser apagada em limpeza de sessão — é o que evita duplicar
 * o dispositivo a cada visita.
 */
const DEVICE_KEY = 'troia-device-id';
const PENDING_PREFIX = 'troia-pending-device-event-';

export type PendingDeviceEvent = 'pwa-install';

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Navegadores antigos / contexto sem crypto.randomUUID
  return `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getOrCreateDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    // localStorage indisponível (modo privado / iframe bloqueado): ID efêmero.
    return randomId();
  }
}

/**
 * O evento pode acontecer ANTES de existir contato (a pessoa instala pela
 * landing e só depois preenche o formulário), então fica pendente até o
 * `register_visitor` responder com o visitorId.
 */
export function markPendingDeviceEvent(channelId: string, event: PendingDeviceEvent): void {
  try {
    localStorage.setItem(`${PENDING_PREFIX}${channelId}`, event);
  } catch {
    // sem storage, sem captura — o chat segue normal
  }
}

/** Lê e limpa o evento pendente (consumo único). */
export function takePendingDeviceEvent(channelId: string): PendingDeviceEvent | null {
  try {
    const value = localStorage.getItem(`${PENDING_PREFIX}${channelId}`);
    if (!value) return null;
    localStorage.removeItem(`${PENDING_PREFIX}${channelId}`);
    return value as PendingDeviceEvent;
  } catch {
    return null;
  }
}
